import Cookies from 'universal-cookie';
import React, { useEffect, useState, useRef } from 'react';
import { codes } from '../../../../constants/country-codes';
import { colors } from '../../../../style-utils/colors';
import { Text } from '../../../../style-utils/text';
import { RightArrow } from '../../../icons/right-arrow';
import { Spinner } from '../../../icons/spinner';
import { UploadImageIcon } from '../../../icons/upload-image';
import {
  AddNewUserForm,
  AddNewUserFormContainer,
  SaveButton,
  ButtonTitle,
  CountryCodeContainer,
  CountryCodeSelect,
  DashboardContent,
  ImageUploadContainer,
  InputContainer,
  Label,
  StyledUserInput,
  Title,
  ButtonsContainer,
  CancelButton,
  ErrorLabel,
  eyeIconStyles,
  StyledSelectMenu,
  CheckRingContainer,
  CheckboxesContainerInSelectMenu,
  StyledSelectCheckboxes,
  StyledImage,
  InfoBanner,
} from './dashboard-users.styled';
import { regexValidation } from '../../../../function-utils/regex-validation';
import { fetchAllItems } from '../../../requests/requests';
import {
  ExistingSupervisionOnlyUser,
  NewUser,
} from '../../../../interfaces/user-interfaces';
import { BuildingData } from '../../../../interfaces/building-interfaces';
import { CheckRing } from '../../../icons/check-ring';
import { useNavigate } from 'react-router-dom';
import { useRedirectOnBack } from '../../../hooks/use-redirect-on-back';
import { apiRoute } from '../../../../constants/api-constants';
import axios, { AxiosError } from 'axios';
import { resizeImage } from '../../../utils/resize-image';

const newUserInitialState: NewUser = {
  user_image: null,
  full_name: '',
  email: '',
  password: '',
  property_ids: [],
  phone_number: '',
  role: 'admin',
};

// Matches supervision-backend's BuildingService.MISSING_LEGACY_FIELD_PLACEHOLDER — its
// own Building form has no equivalent field for several legacy-required `properties`
// columns (address included), so a building created there can carry this exact sentinel
// instead of a real address. Same cross-app magic-string coupling as the phone number
// placeholder — see ../../CLAUDE.md's Shared Database section.
const MISSING_BUILDING_FIELD_PLACEHOLDER = 'TBD - NEEDS CUSTOMER INPUT';

const displayableAddress = (address: string) =>
  address === MISSING_BUILDING_FIELD_PLACEHOLDER ? null : address;

export const DashboardAddNewUser = () => {
  const [newUserData, setNewUserData] = useState<NewUser>(newUserInitialState);
  const [countryCode, setCountryCode] = useState<string>('+1');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [invalidFieldsInputArray, setInvalidFieldsInputArray] = useState<
    string[]
  >([]);
  const [properties, setProperties] = useState<BuildingData[]>([]);
  const [isPropertiesCheckboxesOpened, setIsPropertiesCheckboxesOpened] =
    useState<boolean>(false);
  const checkboxesContainerRef = useRef<HTMLDivElement | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [existingEmailAddress, setExistingEmailAddress] =
    useState<boolean>(false);
  // Set when add-new-user 409s because the email already belongs to a supervision-only
  // account (role IS NULL) — pivots this form into granting that same person a
  // Concierge role instead of trying (and failing) to create a duplicate.
  const [existingUser, setExistingUser] =
    useState<ExistingSupervisionOnlyUser | null>(null);
  // true when Supervision never collected a real phone number for this person (its Add
  // User form has no phone field) — the Phone input stays editable instead of disabled
  // so the admin can supply a real one while granting Concierge access. Stays true even
  // after a real number is typed in (it still reflects "this field is admin-editable,
  // not Supervision-owned"), so it alone isn't the right condition for the hint below.
  const phoneNeedsInput =
    existingUser !== null && existingUser.phone_number === null;
  // Only show the "please enter a real one" hint while the field is genuinely still
  // blank — it was previously tied to phoneNeedsInput alone, so it kept showing even
  // after the admin had already typed a real number.
  const phoneStillMissing = phoneNeedsInput && !newUserData.phone_number;

  const navigate = useNavigate();

  const getPropertiesfromDB = async () => {
    const response = await fetchAllItems('dashboard/buildings');

    setProperties(response as BuildingData[]);
  };

  useEffect(() => {
    getPropertiesfromDB();
  }, []);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const validateNewUserData = (data: any) => {
    const managerFields: string[] = [
      'full_name',
      'email',
      'password',
      'property_ids',
      'phone_number',
      'role',
    ];

    const emptyFields: string[] = [];

    managerFields.forEach(field => {
      if (data[field].length === 0) {
        emptyFields.push(field);
      }

      if (field === 'password' && data.password.length < 8) {
        emptyFields.push('password');
      }
    });

    const emailValidation = regexValidation('email', newUserData.email);

    if (!emailValidation.isValidEmail) {
      emptyFields.push('email');
    }

    return emptyFields;
  };

  const handleCountryCode = (event: React.ChangeEvent<HTMLSelectElement>) => {
    newUserData.phone_number = '';
    setCountryCode(event.target.value);
  };

  const handleInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    if (name === 'user_image') {
      // Check if the event is from file input and has files
      if (event.target instanceof HTMLInputElement && event.target.files) {
        const file = event.target.files[0];
        if (file) {
          setNewUserData({ ...newUserData, [name]: file });
        }
      }
    } else if (name === 'role') {
      // Handle changes for role input
      setNewUserData({ ...newUserData, [name]: value });
    } else if (name === 'phone_number') {
      // Validate and format phone number
      let numericValue = value.replace(/\D/g, '');
      numericValue = '+' + numericValue;
      setNewUserData({ ...newUserData, [name]: numericValue });
    } else if (name === 'password') {
      // Trim password input
      const trimmedPassword = value.trim();
      setNewUserData({ ...newUserData, [name]: trimmedPassword });
    } else {
      // Handle all other inputs
      setNewUserData({ ...newUserData, [name]: value });
    }
  };

  const onSubmit: React.FormEventHandler<HTMLButtonElement> = async e => {
    e.preventDefault();
    setIsLoading(true);

    if (existingUser) {
      // Granting a Concierge role to an already-existing supervision-only row — only
      // role/property_ids (and phone_number, when the admin filled in a real one to
      // replace Supervision's placeholder) are submitted, nothing else is touched.
      try {
        const token = new Cookies().get('authorization_token');
        const response = await axios.put(
          `${apiRoute}/dashboard/users/grant-role`,
          {
            id: existingUser.id,
            role: newUserData.role,
            property_ids: newUserData.property_ids,
            phone_number: newUserData.phone_number,
          },
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          }
        );

        if (response.status === 200) {
          setExistingUser(null);
          setNewUserData(newUserInitialState);
          setInvalidFieldsInputArray([]);
          navigate('users');
        } else {
          console.error('Unexpected response status:', response.status);
        }
      } catch (error) {
        console.error('Error granting Concierge access:', error);
      } finally {
        setIsLoading(false);
      }

      return;
    }

    const invalidFields = validateNewUserData(newUserData);
    setInvalidFieldsInputArray(invalidFields);

    if (invalidFields.length === 0) {
      try {
        const formData = new FormData();

        if (newUserData.user_image instanceof File) {
          const resizedImage = await resizeImage(newUserData.user_image);
          formData.append('user_image', resizedImage);
        } else {
          // console.log('No file selected or incorrect file format');
        }

        formData.append('full_name', newUserData.full_name);
        formData.append('email', newUserData.email);
        formData.append('password', newUserData.password);
        formData.append(
          'property_ids',
          JSON.stringify(newUserData.property_ids)
        );
        formData.append('phone_number', newUserData.phone_number);
        formData.append('role', newUserData.role);

        const token = new Cookies().get('authorization_token');
        const response = await axios.post(
          `${apiRoute}/dashboard/users/add-new-user`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          }
        );

        if (response.status === 200) {
          // console.log('User successfully added:', response.data);
          setExistingEmailAddress(false);
          setNewUserData(newUserInitialState);
          setInvalidFieldsInputArray([]);
          navigate('users');
        } else {
          console.error('Unexpected response status:', response.status);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          console.error('Axios error:', axiosError.message);
          if (axiosError.response) {
            console.error('Response error:', axiosError.response.data);

            if (
              axios.isAxiosError(error) &&
              error.response &&
              error.response.data
            ) {
              const responseData = error.response.data;
              if (
                typeof responseData === 'object' &&
                responseData !== null &&
                'existingUserId' in responseData &&
                'existingUser' in responseData
              ) {
                setExistingUser(
                  (
                    responseData as {
                      existingUser: ExistingSupervisionOnlyUser;
                    }
                  ).existingUser
                );
              } else if (
                typeof responseData === 'object' &&
                responseData !== null &&
                'message' in responseData
              ) {
                // console.log(responseData.message);
                if (
                  responseData.message === 'This email address already exists'
                ) {
                  setExistingEmailAddress(true);
                }
              }
            } else {
              console.error('An unexpected error occurred:', error.message);
            }
          } else if (axiosError.request) {
            console.error('Request error:', axiosError.request);
          }
        } else {
          console.error('Non-Axios error:', error);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      // console.log('Form validation failed');
      setIsLoading(false);
    }
  };

  const showCheckboxes = () => {
    setIsPropertiesCheckboxesOpened(!isPropertiesCheckboxesOpened);
  };

  const clearData = () => {
    setNewUserData(newUserInitialState);
    setNewUserData(prevState => ({
      ...prevState,
      property_ids: [],
    }));
    setInvalidFieldsInputArray([]);
    setExistingUser(null);
    setExistingEmailAddress(false);
    navigate('users');
  };

  useEffect(() => {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any) {
      if (
        checkboxesContainerRef.current &&
        !checkboxesContainerRef.current.contains(event.target)
      ) {
        // Clicked outside the container, so close it
        setIsPropertiesCheckboxesOpened(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleCheckboxChange = (propertyId: string, isChecked: boolean) => {
    if (newUserData.role === 'employee') {
      if (isChecked) {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [Number(propertyId)],
        }));
      } else {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [
            ...newUserData.property_ids.filter(
              item => item !== Number(propertyId)
            ),
          ],
        }));
      }
    } else {
      if (isChecked) {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [...newUserData.property_ids, Number(propertyId)],
        }));
      } else {
        setNewUserData(newUserData => ({
          ...newUserData,
          property_ids: [
            ...newUserData.property_ids.filter(
              item => item !== Number(propertyId)
            ),
          ],
        }));
      }
    }
  };

  useEffect(() => {
    // Grant-access mode (existingUser set) manages its own fields in the effect below
    // instead — this whole-form reset is only for the normal create flow.
    if (existingUser) {
      return;
    }

    setNewUserData(prev => ({ ...newUserInitialState, role: prev.role }));
  }, [newUserData.role, existingUser]);

  useEffect(() => {
    if (!existingUser) {
      return;
    }

    // Only overwrite fields Supervision actually has real data for (they're about to
    // become disabled inputs and must show the truth) — property_ids and role are left
    // exactly as the admin already had them regardless. phone_number only gets
    // overwritten if Supervision actually had a real one; when it's the placeholder
    // (null here, see the backend's conversion), the admin's own typed value is kept.
    // Confirmed by a real report: clicking Save while this same submission triggered the
    // conflict was silently wiping a phone number and property selection the admin had
    // already filled in, since the old version of this effect reset the whole form to
    // blank on every existingUser/role change.
    setNewUserData(prev => ({
      ...prev,
      full_name: existingUser.full_name,
      email: existingUser.email,
      phone_number: existingUser.phone_number ?? prev.phone_number,
    }));
  }, [existingUser]);

  useRedirectOnBack('/dashboard');

  useEffect(() => {
    if (newUserData.user_image !== null) {
      const reader = new FileReader();

      reader.onload = event => {
        if (event.target) {
          setImageSrc(event.target.result as string);
        }
      };

      reader.readAsDataURL(newUserData.user_image);
    }
  }, [newUserData.user_image]);

  return (
    <DashboardContent id="dashboard-add-new-user-content" isAddNewUser={true}>
      <Title>
        <Text mt={16} ml={16} mb={0} fontSize={28} fontWeight={700}>
          Add new user
        </Text>
        <Text mt={6} ml={16} mb={0} fontSize={14} color={`${colors.grey}`}>
          Please fill in the form
        </Text>
      </Title>

      <AddNewUserFormContainer>
        <AddNewUserForm>
          {isLoading && <Spinner isAddNewPropertyForm={true} />}
          {existingUser && (
            <InfoBanner>
              <strong>{existingUser.full_name}</strong> already has a
              Supervision-only account with this email. Showing their record
              instead — choose a role below to grant Concierge access.
            </InfoBanner>
          )}
          <InputContainer isRoleField={true}>
            <CheckRingContainer>
              <CheckRing />
            </CheckRingContainer>
            <StyledSelectMenu
              name="role"
              value={newUserData.role}
              onChange={handleInputChange}
              isRoleField={true}
            >
              <option value={'admin'}>admin</option>
              <option value={'manager'}>manager</option>
              <option value={'employee'}>employee</option>
            </StyledSelectMenu>
          </InputContainer>
          <InputContainer isImageUpload={true}>
            <ImageUploadContainer>
              {imageSrc !== null ? (
                <StyledImage src={imageSrc} alt="User Image" isAddNewUserForm />
              ) : (
                <UploadImageIcon />
              )}
              <Label isImageUpload={true}>Upload photo</Label>
            </ImageUploadContainer>

            <StyledUserInput
              type="file"
              name="user_image"
              accept="image/*"
              onChange={handleInputChange}
              placeholder={'Enter image url'}
              isImageUpload
            />
          </InputContainer>
          {invalidFieldsInputArray.includes('user_image') && (
            <ErrorLabel>Please upload a photo*</ErrorLabel>
          )}
          <InputContainer>
            <Label>User name</Label>
            <StyledUserInput
              type="text"
              name="full_name"
              value={newUserData.full_name}
              placeholder={'Enter first and last name'}
              onChange={handleInputChange}
              disabled={existingUser !== null}
            />
          </InputContainer>
          {invalidFieldsInputArray.includes('full_name') && (
            <ErrorLabel>Please enter first and last name*</ErrorLabel>
          )}
          <InputContainer>
            <Label>Email</Label>
            <StyledUserInput
              type="email"
              name="email"
              value={newUserData.email}
              placeholder={'Enter email address'}
              onChange={handleInputChange}
              disabled={existingUser !== null}
            />
          </InputContainer>
          {invalidFieldsInputArray.includes('email') && (
            <ErrorLabel>Please enter a valid email address*</ErrorLabel>
          )}
          {existingEmailAddress && (
            <ErrorLabel>
              This email address is already taken. Please try another.
            </ErrorLabel>
          )}
          {!existingUser && (
            <InputContainer isPasswordInput={true}>
              <Label>Password</Label>
              <StyledUserInput
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={newUserData.password}
                placeholder={'Enter password'}
                onChange={handleInputChange}
                style={{ position: 'relative' }}
              />
              <i
                style={eyeIconStyles}
                onClick={() => setShowPassword(!showPassword)}
                className={showPassword ? 'far fa-eye' : `far fa-eye-slash`}
              />
            </InputContainer>
          )}
          {!existingUser && invalidFieldsInputArray.includes('password') && (
            <ErrorLabel>
              Your password must be at least 8 characters*
            </ErrorLabel>
          )}
          <InputContainer>
            <Label>Phone</Label>
            <CountryCodeContainer>
              <CountryCodeSelect
                id="country-code"
                onChange={e => handleCountryCode(e)}
                name="countryCode"
                value={countryCode}
                disabled={existingUser !== null && !phoneNeedsInput}
              >
                {Object.entries(codes).map(([country, code]) => (
                  <option key={code} value={code}>
                    {country}
                  </option>
                ))}
              </CountryCodeSelect>

              <StyledUserInput
                type="text"
                name="phone_number"
                value={newUserData.phone_number || countryCode}
                onChange={handleInputChange}
                isCountryCodeInput={true}
                disabled={existingUser !== null && !phoneNeedsInput}
              />
            </CountryCodeContainer>
          </InputContainer>
          {phoneStillMissing && (
            <ErrorLabel>
              Supervision never collected a phone number for this person —
              please enter a real one.
            </ErrorLabel>
          )}
          {invalidFieldsInputArray.includes('phone_number') && (
            <ErrorLabel>Please enter phone number*</ErrorLabel>
          )}{' '}
          <InputContainer>
            <Label>Property selection</Label>

            <div style={{ position: 'relative' }} ref={checkboxesContainerRef}>
              <StyledSelectCheckboxes onClick={showCheckboxes}>
                {newUserData.property_ids.length > 0
                  ? (() => {
                      const selected = properties.filter(
                        prop => Number(prop.id) === newUserData.property_ids[0]
                      )[0];
                      const address = displayableAddress(selected.address);

                      return address
                        ? `${selected.property_name}, ${address}`
                        : selected.property_name;
                    })()
                  : 'Property selection'}
              </StyledSelectCheckboxes>
              {isPropertiesCheckboxesOpened && (
                <CheckboxesContainerInSelectMenu>
                  {properties.map((property, i) => {
                    const address = displayableAddress(property.address);

                    return (
                      <label key={i} style={{ marginBottom: '3px' }}>
                        <input
                          name="property_ids"
                          type="checkbox"
                          value={property.id}
                          checked={newUserData.property_ids.includes(
                            Number(property.id) as never
                          )}
                          onChange={e =>
                            handleCheckboxChange(property.id, e.target.checked)
                          }
                        />
                        {property.property_name}
                        {address ? `, ${address}` : ''}
                      </label>
                    );
                  })}
                </CheckboxesContainerInSelectMenu>
              )}
            </div>
          </InputContainer>
          {invalidFieldsInputArray.includes('property_ids') && (
            <ErrorLabel>Please select at least one property*</ErrorLabel>
          )}
        </AddNewUserForm>
      </AddNewUserFormContainer>
      <ButtonsContainer>
        <CancelButton onClick={() => clearData()}>
          <ButtonTitle>Cancel</ButtonTitle>
        </CancelButton>
        <SaveButton onClick={onSubmit} isWide={existingUser !== null}>
          <ButtonTitle>
            {existingUser ? 'Grant Concierge Access' : 'Save'}
          </ButtonTitle>
          <RightArrow />
        </SaveButton>
      </ButtonsContainer>
    </DashboardContent>
  );
};
